import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLangStore } from "@app/dashboard/store/lang";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { EMPTY_VALUE, THREE_SECONDS } from "@app/dashboard/constants/shared";
import { getServerUrl } from "@app/dashboard/utils/serverUrls";
import type { IShowIframeFormModal } from "@app/dashboard/types/innerLayout/iframes/show-iframe";
import type { IDuplicateIframePayload } from "@app/dashboard/types/innerLayout/iframes/duplicate-iframe";
import type { IEditIframePayload } from "@app/dashboard/types/innerLayout/iframes/edit-iframe";
import { getIframes } from "@app/dashboard/services/inner_layout/iframes/get-iframes";
import { getPreviewDetails } from "@app/dashboard/services/inner_layout/iframes/get-preview";
import { deleteIframe } from "@app/dashboard/services/inner_layout/iframes/delete-iframe";
import { duplicateIframe } from "@app/dashboard/services/inner_layout/iframes/duplicate-iframe";
import { editIframe } from "@app/dashboard/services/inner_layout/iframes/edit-iframe";
import { PageStatus, SkeletonLoader } from "@unified-checkout/design-system";
import { CircleExclamation } from "libs/design-system/src/assets/Icons";
import { IframeCard } from "@app/dashboard/views/pages/inner_layout/Iframes/components/IframeCard";
import { Header } from "./Header";
import { DeleteModal } from "./components/IframeModal/Modals/DeleteModal";
import { EditModal } from "./components/IframeModal/Modals/EditModal";
import { DuplicateModal } from "./components/IframeModal/Modals/DuplicateModal";
import styles from "./iframe.module.scss";

export const Iframes = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const isDesktop = screenWidth <= 1025;

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [selectedIframeId, setSelectedIframeId] = useState<number>();
  const [selectedPreviewId, setSelectedPreviewId] = useState<number | null>(null);
  const [iframeForm, setIframeForm] = useState({
    name: "",
    description: "",
    content_html: "",
    content_css: "",
    content_js: "",
  });

  const {
    isLoading,
    isError,
    data: response,
    refetch,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_IFRAMES, lang],
    queryFn: () => getIframes(),
    refetchOnMount: "always",
  });
  const {
    mutate: previewMutate,
    isPending: isPreviewPending,
  } = useMutation({
    mutationKey: [QUERY_KEYS.GET_PREVIEW],
    mutationFn: (iframeId: number) => getPreviewDetails(iframeId),
    onSuccess: (response) => {
      const newWindow = window.open('', '_blank');
      if (!newWindow) return;

      const serverUrl = getServerUrl();
      const pageContent = response?.data
        .replace(/src="\/static/g, `src="${serverUrl}/static`)
        .replace(/href="\/static/g, `href="${serverUrl}/static`)
        .replace(/url\('\/static/g, `url('${serverUrl}/static`);

      const blob = new Blob([pageContent], { type: 'text/html' });
      const newTabContent = URL.createObjectURL(blob);

      newWindow.location.href = newTabContent;
      setSelectedPreviewId(null)
    },
  });

  const {
    mutate: deleteMutate,
    isPending: isDeletePending,
    isError: isDeleteError,
    isSuccess: isDeleteSuccess,
    reset: resetDelete
  } = useMutation({
    mutationKey: [QUERY_KEYS.DELETE_IFRAME],
    mutationFn: (iframeId: number) => deleteIframe(iframeId),
    onSuccess: () => {
      setSelectedIframeId(undefined);
      clearIframeForm();
      setTimeout(() => {
        setShowDeleteModal(false);
        resetDelete?.();
      }, THREE_SECONDS);
    },
    onError: () => {
      setTimeout(() => {
        setShowDeleteModal(false);
        resetDelete?.();
      }, THREE_SECONDS);
    }
  });

  const {
    mutate: duplicateMutate,
    isPending: isDuplicatePending,
    isError: isDuplicateError,
    isSuccess: isDuplicateSuccess,
    reset: resetDuplicate
  } = useMutation({
    mutationKey: [QUERY_KEYS.DUPLICATE_IFRAME],
    mutationFn: (payload: IDuplicateIframePayload) => duplicateIframe(payload),
    onSuccess: () => {
      setSelectedIframeId(undefined);
      clearIframeForm();
      setTimeout(() => {
        setShowDuplicateModal(false);
        clearIframeForm();
        resetDuplicate?.();
      }, THREE_SECONDS);
    },
    onError: () => {
      setTimeout(() => {
        setShowDuplicateModal(false);
        clearIframeForm();
        resetDuplicate?.();
      }, THREE_SECONDS);
    }
  });

  const {
    mutate: editMutate,
    isPending: isEditPending,
    isError: isEditError,
    isSuccess: isEditSuccess,
    reset: resetEdit
  } = useMutation({
    mutationKey: [QUERY_KEYS.EDIT_IFRAME],
    mutationFn: ({ id, iframeForm }: { id: number; iframeForm: IEditIframePayload }) => editIframe(id, iframeForm),
    onSuccess: () => {
      setSelectedIframeId(undefined);
      clearIframeForm();
      setTimeout(() => {
        setShowEditModal(false);
        resetEdit?.();
      }, THREE_SECONDS);
    },
    onError: () => {
      setTimeout(() => {
        setShowEditModal(false);
        resetEdit?.();
      }, THREE_SECONDS);
    }
  });


  const iframesData = response?.data?.results
  const countOfIframe = response?.data?.count;

  const clearIframeForm = () => {
    setIframeForm({
      name: "",
      description: "",
      content_html: "",
      content_css: "",
      content_js: "",
    });
  };

  const handleDeleteClick = (iframeId: number) => {
    setSelectedIframeId(iframeId);
    setShowDeleteModal(true);
  };


  const handleEditClick = ({
    id,
    name,
    description,
    content_html,
    content_css,
    content_js,
  }: IShowIframeFormModal) => {
    setSelectedIframeId(id as number);
    setIframeForm({
      name,
      description,
      content_html,
      content_css,
      content_js,
    });
    setShowEditModal(true);
  };

  const handleDuplicateClick = ({
    id,
    name,
    description,
    content_html,
    content_css,
    content_js
  }: IShowIframeFormModal) => {
    setSelectedIframeId(id as number);
    setIframeForm({
      name,
      description,
      content_html,
      content_css,
      content_js,
    });
    setShowDuplicateModal(true);
  };

  const handlePreviewClick = (iframeId: number) => {
    setSelectedPreviewId(iframeId);
    previewMutate(iframeId);
  };


  const renderIframeCards = () => {
    if (isLoading || isFetching) {
      return Array.from({ length: 6 }).map((_, index) => (
        <SkeletonLoader
          key={`skeleton-${index}`}
          type="bordered_box"
          noOfRows={6}
          height="20rem"
        />
      ));
    }

    return iframesData?.map((iframe: any, index: number) => (
      <IframeCard
        key={index}
        title={`Iframe ${iframe.id}`}
        itemsList={[
          { label: t('innerLayout.iFramesPage.iframeCard.name'), value: iframe.name || EMPTY_VALUE },
          { label: t('innerLayout.iFramesPage.iframeCard.desc'), value: iframe.description || EMPTY_VALUE },
          {
            label: t('innerLayout.iFramesPage.iframeCard.dataCreated'),
            value: iframe.created_at
              ? new Date(iframe.created_at).toLocaleString(lang, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
              : EMPTY_VALUE,
          },
          {
            label: t('innerLayout.iFramesPage.iframeCard.iframeLink'),
            value: `https://accept-alpha.paymob.com/api/acceptance/iframes/${iframe.id}?payment_token={payment_key_obtained_previously}`,
          },
        ]}
        actions={{
          // onDelete: () => handleDeleteClick(iframe.id),
          // onEdit: () => handleEditClick(iframe),
          // onDuplicate: () => handleDuplicateClick(iframe),
          onPreview: () => handlePreviewClick(iframe.id),
        }}
        disabled={isPreviewPending}
        selectedIframeId={selectedPreviewId === iframe.id}
      />
    ));
  };
  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    clearIframeForm()
    resetDuplicate?.();
  }


  const deleteModalProps = {
    open: showDeleteModal,
    isLoading: isDeletePending,
    isError: isDeleteError,
    selectedIframeId: selectedIframeId,
    isSuccess: isDeleteSuccess,
    onClose: () => setShowDeleteModal(false),
    onConfirm: () => deleteMutate(selectedIframeId as number)
  }

  const editModalProps = {
    open: showEditModal,
    isLoading: isEditPending,
    isError: isEditError,
    isSuccess: isEditSuccess,
    iframeForm: iframeForm,
    onClose: () => setShowEditModal(false),
    onConfirm: () =>
      editMutate({
        id: selectedIframeId as number,
        iframeForm,
      }),
    onFormChange: setIframeForm
  }

  const duplicateModalProps = {
    open: showDuplicateModal,
    isLoading: isDuplicatePending,
    isError: isDuplicateError,
    isSuccess: isDuplicateSuccess,
    iframeForm: iframeForm,
    onClose: () => handleCloseDuplicateModal(),
    onConfirm: () => duplicateMutate(iframeForm),
    onFormChange: setIframeForm
  }
  // Filter out empty boxes
  const filteredBoxes = renderIframeCards() || []

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_: any, index: number) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_: any, index: number) => index % 2 !== 0); // Odd-indexed items

  const content = <>
    {
      isDesktop ?
        <>
          {filteredBoxes}
        </>
        :
        [col1Boxes, col2Boxes].map((colContent, index) =>
          <div className={styles["iframe__main__col"]} key={`iframe-col-${index}`}>
            {colContent}
          </div>
        )
    }
  </>;

  return (
    <div className={styles["iframe"]}>
      {
        isError ?
          <PageStatus
            status="error"
            title={t('innerLayout.errors.smthWentWrong')}
            description={t('innerLayout.errors.dataRetrievalErr')}
            icon={CircleExclamation}
          />
          :
          <>
            <Header
              id={Number(id)}
              count={countOfIframe}
              //data={iframesData}
              isLoading={isLoading || isFetching}
              isError={isError}
              refetchData={refetch}
            />

            <div className={styles["iframe__main"]}>
              {content}
            </div>
          </>
      }
      <div className={styles["iframe__modal"]}>
        <DeleteModal {...deleteModalProps} />
        <EditModal {...editModalProps} />
        <DuplicateModal {...duplicateModalProps} />
      </div>
    </div >
  )
}